import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { useConfigSocket } from '../../../shared/hooks/useConfigSocket';
import PersonnelModal from './components/config/modals/PersonnelModal';
import VehiculeModal from './components/config/modals/VehiculeModal';
import CompetenceModal from './components/config/modals/CompetenceModal';
import FonctionArmementModal from './components/config/modals/FonctionArmementModal';
import PersonnelTab from './components/config/tabs/PersonnelTab';
import VehiculesTab from './components/config/tabs/VehiculesTab';
import CompetencesTab from './components/config/tabs/CompetencesTab';
import FonctionsArmementTab from './components/config/tabs/FonctionsArmementTab';
import FormationsTab from './components/config/tabs/FormationsTab';
import FormationModal from './components/config/modals/FormationModal';
import BatimentsTab from './components/config/tabs/BatimentsTab';
import PartenairesTab from './components/config/tabs/PartenairesTab';
import CategoriesMissionTab from './components/config/tabs/CategoriesMissionTab';
import CategoriesSpeTab from './components/config/tabs/CategoriesSpeTab';
import SystemConfigTabsNav, { type SystemConfigTabId } from './components/config/SystemConfigTabsNav';
import { useSystemConfigLoad } from './hooks/useSystemConfigLoad';
import { usePersonnelConfig } from './hooks/usePersonnelConfig';
import { useVehiculeConfig } from './hooks/useVehiculeConfig';
import { useCompetenceConfig } from './hooks/useCompetenceConfig';
import { useFonctionArmementConfig } from './hooks/useFonctionArmementConfig';
import { useFormationConfig } from './hooks/useFormationConfig';
import { useBuildingTypeConfig } from './hooks/useBuildingTypeConfig';
import { usePartenairesConfig } from './hooks/usePartenairesConfig';
import { useCategoriesMissionConfig } from './hooks/useCategoriesMissionConfig';
import { useCategoriesSpeConfig } from './hooks/useCategoriesSpeConfig';
import type { Service } from '../../../shared/types/config';

interface SystemConfigurationProps {
  embedded?: boolean;
}

interface CategorieSpecialiteItem {
  id: number;
  nom: string;
  id_service: number;
  ordre: number;
  actif: number;
  service_nom?: string;
}

/**
 * Page Configuration du système (CM uniquement).
 *
 * Orchestrateur fin : tous les états et handlers métier sont déplacés dans des
 * hooks dédiés (`hooks/use*Config.ts`). Les onglets JSX sont des composants
 * extraits (`components/config/tabs/*Tab.tsx`). Le fichier ne contient ici
 * que le câblage entre les hooks, la nav et le rendu d'onglets.
 */
const SystemConfiguration: React.FC<SystemConfigurationProps> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { t } = usePageTranslation('panel', 'SystemConfiguration');

  // Accès CM uniquement
  const hasAccess = currentUser?.roles_id === 1;

  const [activeTab, setActiveTab] = useState<SystemConfigTabId>('personnel');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [services, setServices] = useState<Service[]>([]);
  const [personnelFilterService, setPersonnelFilterService] = useState<number | 'all'>('all');
  const [categoriesMission, setCategoriesMission] = useState<
    { id: number; nom: string; created_at?: string }[]
  >([]);
  const [categoriesSpecialite, setCategoriesSpecialite] = useState<CategorieSpecialiteItem[]>([]);

  // `loadAllData` est appelé par les handlers des sous-hooks après chaque
  // mutation. Pour casser la dépendance circulaire (les hooks ont besoin de
  // loadAllData, et loadAllData a besoin des setters des hooks), on passe
  // une référence stable qui sera renseignée plus bas.
  const loadAllDataRef = useRef<() => Promise<void>>(async () => {});
  const callLoadAllData = useCallback(() => loadAllDataRef.current(), []);

  const personnel = usePersonnelConfig({
    personnelFilterService,
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });
  const vehicule = useVehiculeConfig({
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });
  const competence = useCompetenceConfig({
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });
  const fonctionArmement = useFonctionArmementConfig({
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });
  const formation = useFormationConfig({
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });
  const building = useBuildingTypeConfig({
    services,
    setError,
    setSuccess,
  });
  const partner = usePartenairesConfig({
    setError,
    setSuccess,
  });
  const missionCat = useCategoriesMissionConfig({
    categoriesMission,
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });
  const catSpe = useCategoriesSpeConfig({
    setError,
    setSuccess,
    loadAllData: callLoadAllData,
  });

  // Chargement des données selon l'onglet actif
  const loadAllData = useSystemConfigLoad({
    activeTab,
    setLoading,
    setError,
    setPersonnelConfigs: personnel.setPersonnelConfigs,
    setVehiculeConfigs: vehicule.setVehiculeConfigs,
    setServices,
    setCompetences: competence.setCompetences,
    setFonctionsArmement: fonctionArmement.setFonctionsArmement,
    setFormations: formation.setFormations,
    setCategoriesMission,
    setCategoriesSpecialite,
    setBuildingTypesConfig: building.setBuildingTypesConfig,
    setPartenairesConfig: partner.setPartenairesConfig,
  });

  // Branche la ref pour que les hooks puissent appeler loadAllData()
  useEffect(() => {
    loadAllDataRef.current = loadAllData;
  }, [loadAllData]);

  useEffect(() => {
    if (hasAccess) {
      void loadAllData();
    }
  }, [hasAccess, activeTab, loadAllData]);

  // Mises à jour temps réel
  useConfigSocket({
    onPersonnelCreated: (data) => {
      if (activeTab === 'personnel') {
        personnel.setPersonnelConfigs((prev) => [...prev, data]);
        setSuccess('Nouveau personnel ajouté (temps réel)');
      }
    },
    onPersonnelUpdated: (data) => {
      if (activeTab === 'personnel') {
        personnel.setPersonnelConfigs((prev) => prev.map((p) => (p.id === data.id ? data : p)));
        setSuccess('Personnel modifié (temps réel)');
      }
    },
    onPersonnelDeleted: (data) => {
      if (activeTab === 'personnel') {
        personnel.setPersonnelConfigs((prev) => prev.filter((p) => p.id !== data.id));
        setSuccess('Personnel supprimé (temps réel)');
      }
    },
    onVehiculeCreated: (data) => {
      if (activeTab === 'vehicules') {
        vehicule.setVehiculeConfigs((prev) => [...prev, data]);
        setSuccess('Nouveau véhicule ajouté (temps réel)');
      }
    },
    onVehiculeUpdated: (data) => {
      if (activeTab === 'vehicules') {
        vehicule.setVehiculeConfigs((prev) => prev.map((v) => (v.id === data.id ? data : v)));
        setSuccess('Véhicule modifié (temps réel)');
      }
    },
    onVehiculeDeleted: (data) => {
      if (activeTab === 'vehicules') {
        vehicule.setVehiculeConfigs((prev) => prev.filter((v) => v.id !== data.id));
        setSuccess('Véhicule supprimé (temps réel)');
      }
    },
    onCompetenceCreated: (data) => {
      competence.setCompetences((prev) => [...prev, data]);
      if (activeTab === 'competences') setSuccess('Nouvelle compétence ajoutée (temps réel)');
    },
    onCompetenceUpdated: (data) => {
      competence.setCompetences((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      if (activeTab === 'competences') setSuccess('Compétence modifiée (temps réel)');
    },
    onCompetenceDeleted: (data) => {
      competence.setCompetences((prev) => prev.filter((c) => c.id !== data.id));
      if (activeTab === 'competences') setSuccess('Compétence supprimée (temps réel)');
    },
  });

  if (!hasAccess) {
    return (
      <div className={embedded ? '' : 'min-h-screen relative overflow-hidden'}>
        {!embedded && <BackgroundImage />}
        {!embedded && <ParticlesBackground />}
        {!embedded && <Navbar />}
        <div className={embedded ? '' : 'relative z-10 pt-24 pb-16'}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 backdrop-blur-sm text-center text-white">
              <h2 className="text-xl font-bold mb-4">{t('accessDenied.title')}</h2>
              <p className="text-slate-400">{t('accessDenied.message')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen relative overflow-hidden'}>
      {!embedded && <BackgroundImage />}
      {!embedded && <ParticlesBackground />}
      {!embedded && <Navbar />}
      <div className={embedded ? '' : 'relative z-10 pt-24 pb-16'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
              <p className="text-slate-400 text-sm">{t('subtitle')}</p>
            </div>
            {!embedded && (
              <button
                onClick={() => navigate('/panel')}
                className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>{t('backToPanel')}</span>
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
              {success}
            </div>
          )}

          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 backdrop-blur-sm">
            <SystemConfigTabsNav activeTab={activeTab} setActiveTab={setActiveTab} t={t} />

            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="mt-6">
                {activeTab === 'personnel' && (
                  <PersonnelTab
                    personnelConfigs={personnel.personnelConfigs}
                    searchPersonnel={personnel.searchPersonnel}
                    onSearchChange={personnel.setSearchPersonnel}
                    filterService={personnelFilterService}
                    onFilterServiceChange={setPersonnelFilterService}
                    onAdd={personnel.handleAddPersonnel}
                    onEdit={personnel.handleEditPersonnel}
                    onDelete={personnel.handleDeletePersonnel}
                    services={services}
                    competences={competence.competences}
                  />
                )}

                {activeTab === 'vehicules' && (
                  <VehiculesTab
                    vehiculeConfigs={vehicule.vehiculeConfigs}
                    searchVehicule={vehicule.searchVehicule}
                    onSearchChange={vehicule.setSearchVehicule}
                    onAdd={vehicule.handleAddVehicule}
                    onEdit={vehicule.handleEditVehicule}
                    onDelete={vehicule.handleDeleteVehicule}
                    services={services}
                    personnelConfigs={personnel.personnelConfigs}
                    competences={competence.competences}
                  />
                )}

                {activeTab === 'competences' && (
                  <CompetencesTab
                    competences={competence.competences}
                    searchCompetence={competence.searchCompetence}
                    onSearchChange={competence.setSearchCompetence}
                    filterType={competence.filterType}
                    onFilterTypeChange={competence.setFilterType}
                    filterService={competence.filterService}
                    onFilterServiceChange={competence.setFilterService}
                    onAdd={competence.handleAddCompetence}
                    onEdit={competence.handleEditCompetence}
                    onDelete={competence.handleDeleteCompetence}
                    services={services}
                  />
                )}

                {activeTab === 'fonctions-armement' && (
                  <FonctionsArmementTab
                    fonctionsArmement={fonctionArmement.fonctionsArmement}
                    searchFonctionsArmement={fonctionArmement.searchFonctionsArmement}
                    onSearchChange={fonctionArmement.setSearchFonctionsArmement}
                    onAdd={fonctionArmement.handleAddFonctionArmement}
                    onEdit={fonctionArmement.handleEditFonctionArmement}
                    onDelete={fonctionArmement.handleDeleteFonctionArmement}
                    competences={competence.competences}
                  />
                )}

                {activeTab === 'formations' && (
                  <FormationsTab
                    formations={formation.formations}
                    searchFormation={formation.searchFormation}
                    onSearchChange={formation.setSearchFormation}
                    onAdd={formation.handleAddFormation}
                    onEdit={formation.handleEditFormation}
                    onDelete={formation.handleDeleteFormation}
                    competences={competence.competences}
                  />
                )}

                {activeTab === 'batiments' && (
                  <BatimentsTab
                    t={t}
                    loading={loading}
                    services={services}
                    buildingForm={building.buildingForm}
                    setBuildingForm={building.setBuildingForm}
                    buildingTypesConfig={building.buildingTypesConfig}
                    editingBuildingId={building.editingBuildingId}
                    savingBuilding={building.savingBuilding}
                    handleSaveBuilding={building.handleSaveBuilding}
                    handleCancelEditBuilding={building.handleCancelEditBuilding}
                    handleEditBuilding={building.handleEditBuilding}
                    handleDeleteBuilding={building.handleDeleteBuilding}
                  />
                )}

                {activeTab === 'partenaires' && (
                  <PartenairesTab
                    t={t}
                    loading={loading}
                    partenairesConfig={partner.partenairesConfig}
                    partnerForm={partner.partnerForm}
                    setPartnerForm={partner.setPartnerForm}
                    editingPartnerId={partner.editingPartnerId}
                    savingPartner={partner.savingPartner}
                    handleSavePartner={partner.handleSavePartner}
                    handleCancelEditPartner={partner.handleCancelEditPartner}
                    handleEditPartner={partner.handleEditPartner}
                    handleDeletePartner={partner.handleDeletePartner}
                  />
                )}

                {activeTab === 'categories-mission' && (
                  <CategoriesMissionTab
                    t={t}
                    loading={loading}
                    categoriesMission={categoriesMission}
                    newCategoryName={missionCat.newCategoryName}
                    setNewCategoryName={missionCat.setNewCategoryName}
                    editingMissionCategoryId={missionCat.editingMissionCategoryId}
                    creatingCategory={missionCat.creatingCategory}
                    handleSaveMissionCategory={missionCat.handleSaveMissionCategory}
                    handleCancelEditMissionCategory={missionCat.handleCancelEditMissionCategory}
                    handleEditMissionCategory={missionCat.handleEditMissionCategory}
                    handleDeleteMissionCategory={missionCat.handleDeleteMissionCategory}
                  />
                )}

                {activeTab === 'categories-spe' && (
                  <CategoriesSpeTab
                    t={t}
                    loading={loading}
                    services={services}
                    categoriesSpecialite={categoriesSpecialite}
                    newCategorieSpeNom={catSpe.newCategorieSpeNom}
                    setNewCategorieSpeNom={catSpe.setNewCategorieSpeNom}
                    newCategorieSpeServiceId={catSpe.newCategorieSpeServiceId}
                    setNewCategorieSpeServiceId={catSpe.setNewCategorieSpeServiceId}
                    creatingCategorieSpe={catSpe.creatingCategorieSpe}
                    handleCreateCategorieSpe={catSpe.handleCreateCategorieSpe}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {personnel.showPersonnelModal && (
        <PersonnelModal
          editingPersonnel={personnel.editingPersonnel}
          personnelForm={personnel.personnelForm}
          setPersonnelForm={personnel.setPersonnelForm}
          services={services}
          competences={competence.competences}
          onClose={() => personnel.setShowPersonnelModal(false)}
          onSave={personnel.handleSavePersonnel}
        />
      )}

      {vehicule.showVehiculeModal && (
        <VehiculeModal
          editingVehicule={vehicule.editingVehicule}
          vehiculeForm={vehicule.vehiculeForm}
          setVehiculeForm={vehicule.setVehiculeForm}
          services={services}
          personnelConfigs={personnel.personnelConfigs}
          competences={competence.competences}
          fonctionsArmement={fonctionArmement.fonctionsArmement}
          onClose={() => vehicule.setShowVehiculeModal(false)}
          onSave={vehicule.handleSaveVehicule}
        />
      )}

      {competence.showCompetenceModal && (
        <CompetenceModal
          editingCompetence={competence.editingCompetence}
          competenceForm={competence.competenceForm}
          setCompetenceForm={competence.setCompetenceForm}
          services={services}
          onClose={() => competence.setShowCompetenceModal(false)}
          onSave={competence.handleSaveCompetence}
        />
      )}

      {fonctionArmement.showFonctionArmementModal && (
        <FonctionArmementModal
          editingFonctionArmement={fonctionArmement.editingFonctionArmement}
          fonctionArmementForm={fonctionArmement.fonctionArmementForm}
          setFonctionArmementForm={fonctionArmement.setFonctionArmementForm}
          competences={competence.competences}
          services={services}
          onClose={() => fonctionArmement.setShowFonctionArmementModal(false)}
          onSave={fonctionArmement.handleSaveFonctionArmement}
        />
      )}

      {formation.showFormationModal && (
        <FormationModal
          editingFormation={formation.editingFormation}
          formationForm={formation.formationForm}
          setFormationForm={formation.setFormationForm}
          competences={competence.competences}
          onClose={() => formation.setShowFormationModal(false)}
          onSave={formation.handleSaveFormation}
        />
      )}
    </div>
  );
};

export default SystemConfiguration;
